import { useState } from 'react';
import { countryByIso } from '../../utils/countries';
import CountrySelect from '../CountrySelect';
import Modal from '../ui/Modal';

const DEFAULT_COUNTRY = 'US';
const E164 = /^\+[1-9]\d{7,14}$/;

/**
 * Turns the dial code plus typed number into E.164, or says why it cannot. The backend normalises
 * again, so this only has to catch obvious mistakes. A typed "+" number wins over the selector.
 * @param {string} iso
 * @param {string} raw
 * @returns {{phone?: string, error?: string}}
 */
export function buildPhone(iso, raw) {
  const text = String(raw || '').trim();
  if (!text) return { error: 'Enter a phone number' };
  if (/[a-z]/i.test(text)) return { error: 'That phone number does not look valid (letters are not allowed)' };
  const digits = text.replace(/\D/g, '');
  let phone;
  if (text.startsWith('+')) {
    phone = `+${digits}`;
  } else {
    const dial = countryByIso(iso)?.dial;
    if (!dial) return { error: 'Choose a country for this phone number' };
    const national =
      dial === '+1' ? (digits.length === 11 ? digits.replace(/^1/, '') : digits) : digits.replace(/^0+/, '');
    phone = `${dial}${national}`;
  }
  return E164.test(phone) ? { phone } : { error: 'That phone number does not look valid' };
}

/**
 * Start a chat with any number: a dial code paired with the number, plus an optional name. Opens the
 * conversation via `onStart(e164, name)`; the inbox decides whether it already exists.
 * @param {{onStart: (phone: string, name: string) => void, onClose: () => void}} props
 */
const NewChatDialog = ({ onStart, onClose }) => {
  const [iso, setIso] = useState(DEFAULT_COUNTRY);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const result = buildPhone(iso, number);
    if (result.error) {
      setError(result.error);
      return;
    }
    onStart(/** @type {string} */ (result.phone), name.trim());
  };

  return (
    <Modal
      onClose={onClose}
      maxWidth="sm"
      fullHeight={false}
      className="max-h-[80vh]"
      hasUnsavedChanges={!!(number || name)}
    >
      <form onSubmit={submit} className="space-y-4 p-6">
        <h2 className="text-lg font-semibold text-foreground">New chat</h2>
        <div>
          <div className="flex gap-2">
            <CountrySelect variant="phone" ariaLabel="Phone country" value={iso} onChange={setIso} />
            <input
              type="tel"
              inputMode="tel"
              aria-label="WhatsApp number"
              placeholder="WhatsApp number"
              value={number}
              onChange={(e) => {
                setNumber(e.target.value);
                setError('');
              }}
              className="min-w-0 flex-1 rounded-lg border border-input px-3 py-3 text-base"
            />
          </div>
          {error && (
            <p role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}
        </div>
        <input
          type="text"
          aria-label="Contact name"
          placeholder="Name (optional)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-lg border border-input px-3 py-3 text-base"
        />
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm text-primary-foreground bg-primary rounded-lg hover:bg-primary/90"
          >
            Start chat
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default NewChatDialog;
